import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { UiToastMessageService } from 'ui-shared';
import { HrFormLoadingVisibility } from '../base-form/hr-form-loading-visibility';
import * as logger from '../hr-logger';
import { TranslateService } from '@ngx-translate/core';
import { HrCurrentFormKeys } from './hr-current-form-keys.service';
import { HrConstraintExceptionKey } from '../translate/hr-constraint-exception-key';

@Injectable({
    providedIn: 'root',
})
export class ServiceCall {
    constructor(
        private currentFormKeys: HrCurrentFormKeys,
        private toastMessage: UiToastMessageService,
        private translateService: TranslateService,
    ) {}

    callListService<T>(
        page: HrFormLoadingVisibility,
        service: Observable<T>,
        onSuccess: (value: T) => void,
        param?: ServiceCallParam<T>,
    ) {
        if (param == null) {
            param = {};
        }
        param.loading = true;
        this.callService(page, service, onSuccess, param);
    }

    callFormService<T>(
        page: HrFormLoadingVisibility,
        service: Observable<T>,
        onSuccess: (value: T) => void,
        param?: ServiceCallParam<T>,
    ) {
        if (param == null) {
            param = {};
        }
        param.loading = false;
        this.callService(page, service, onSuccess, param);
    }

    private callService<T>(
        page: HrFormLoadingVisibility,
        service: Observable<T>,
        onSuccess: (value: T) => void,
        param?: ServiceCallParam<T>,
    ) {
        const loading = param?.loading ?? true;
        const showError = param?.showError ?? true;
        if (loading) {
            page.showLoading();
        }
        let uid = logger.getUniqueId();
        let timeTag = logger.HR_ALL_SERVICE_COMPLETE_TIME + '__' + uid;
        console.time(timeTag);
        service.subscribe(
            (response) => {
                logger.logServiceSuccessResponse(response, uid);

                this.showSuccessMessage(param);
                if (onSuccess != null) {
                    onSuccess(response);
                }
            },
            (err) => {
                logger.logServiceErrorResponse(err, uid);

                if (showError) {
                    const errorCode = err.error.errorDetail?.errorCode;
                    const errorMessage = err.error.errorDetail?.errorMessage;
                    let errArguments: string[] = err.error.errorDetail?.errorArguments;
                    if (errorCode === 'ERR_HBRNT_CONST_VIOLAT_001') {
                        if (errArguments?.length > 0) {
                            errArguments = errArguments[0].split(', ');
                            let errorListMessage = errArguments
                                .map((it) => this.translateService.instant(HrConstraintExceptionKey.prefix + it))
                                .join(', ');
                            this.shorErrorMessage(this.translateService.instant(errorCode, [errorListMessage]));
                        }
                    } else {
                        const errorArguments = err.error.errorDetail?.errorArguments;
                        this.shorErrorMessage(
                            this.translateService.instant(errorCode ?? errorMessage,
                                errorArguments ? errorArguments : [errorMessage]),
                        );
                    }
                }
                if (param?.onError != null) {
                    param?.onError(err);
                }
            },
            () => {
                logger.logServiceComplete(timeTag);

                if (loading) {
                    page.hideLoading();
                }
                if (param?.onComplete != null) {
                    param?.onComplete();
                }
            },
        );
    }

    private showSuccessMessage<T>(param: ServiceCallParam<T>) {
        if (param?.editMessage != null) {
            this.toastMessage.showEditSuccess(this.translateService.instant(param?.editMessage ?? ''));
        } else if (param?.deleteMessage != null) {
            this.toastMessage.showDeleteSuccess(this.translateService.instant(param?.deleteMessage ?? ''));
        } else if (param?.saveMessage != null) {
            this.toastMessage.showSaveSuccess(this.translateService.instant(param?.saveMessage ?? ''));
        }
    }

    private shorErrorMessage(errorMessage: string) {
        try {
            const msg = JSON.parse(errorMessage);
            if ('string' === typeof msg) {
                this.toastMessage.showError(errorMessage.toString(), 8000);
            } else {
                let errMsgList = this.getGroupedErrorMessages(msg);
                let allMsg = this.getFlatErrorMessages(errMsgList);
                allMsg = allMsg.sort((n1, n2) => n1.localeCompare(n2));
                this.toastMessage.showError(allMsg.join('\n'), 8000);
            }
        } catch (ex) {
            this.toastMessage.showError(errorMessage, 8000);
        }
    }

    private getGroupedErrorMessages(msg: any): Map<string, Array<string>> {
        let errMsgList = new Map<string, Array<string>>();
        for (let item of msg) {
            if (item.fieldName != null) {
                const fieldResource = this.currentFormKeys.translateKey(item.fieldName);
                if (errMsgList.has(item.message)) {
                    const arr = errMsgList.get(item.message);
                    errMsgList.set(item.message, [...arr, `'${fieldResource}'`]);
                } else {
                    errMsgList.set(item.message, [`'${fieldResource}'`]);
                }
            } else if (item.message != null) {
                errMsgList.set(item.message, []);
            }
        }
        return errMsgList;
    }

    private getFlatErrorMessages(errMsgList: Map<string, Array<string>>): Array<string> {
        let allMsg: Array<string> = [];
        for (let key of errMsgList.keys()) {
            const list = errMsgList.get(key).sort((n1, n2) => n1.localeCompare(n2));
            if (list.length > 0) {
                allMsg.push(`${list.join(', ')} ${key}`);
            } else {
                allMsg.push(key);
            }
        }
        return allMsg;
    }
}

export interface ServiceCallParam<T> {
    onError?: (error: any) => void;
    onComplete?: () => void;
    loading?: boolean;
    showError?: boolean;
    editMessage?: string;
    deleteMessage?: string;
    saveMessage?: string;
}
